import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import {
  catchError,
  combineLatest,
  concatMap,
  filter,
  first,
  groupBy,
  map,
  mergeMap,
  of,
  repeat,
  startWith,
  withLatestFrom,
} from 'rxjs';

import {
  ApplyWorkflowNodePayload,
  PageContextFacade,
  runtimeActions,
  runtimeSelectors,
  UrlParametersFacade,
  WorkflowStepDebugger,
} from '@blaze-runtime/engine-data-access';
import { CanvasElement, getNamespaceId, RuntimeErrorMessage } from '@blaze-runtime/engine-domain';
import {
  DatasetResponse,
  ExecuteWorkflowResponse,
  ServerErrorResponse,
} from '@blaze-shared/api-contracts-domain';
import { hasGroupedData } from '@blaze-shared/datasets-domain';
import { getActiveBinding } from '@blaze-shared/elements-domain';
import { IntelliSenseService } from '@blaze-shared/intellisense-feature';
import { JobsFacade } from '@blaze-shared/jobs-data-access';
import { isServerErrorResponse, WorkflowLoggerService } from '@blaze-shared/platform-data-access';
import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  convertWorkflowSchemaToServerDatasetSchema,
  isWorkflowSchema,
} from '@blaze-shared/schema-domain';
import {
  isDeepEqual,
  isEmptyish,
  isEqual,
  isNullish,
  shallowCopy,
} from '@blaze-shared/shared-util';
import { WorkflowsApiService } from '@blaze-shared/workflow-data-access';
import {
  findFirstNodeInWorkflow,
  getValueByPath,
  getWorkflowNodeById,
  isDeleteNode,
  isGetNode,
  isPolledWorkflow,
  MAIN_INPUT_NAME,
  shouldWorkflowSubscribeToChanges,
  WorkflowPhase,
} from '@blaze-shared/workflow-domain';

import { hasDataProps, hasProps } from '../../canvas-elements/element-model/element-props-guards';
import { isElementType } from '../../canvas-elements/element-model/element-type-guards';
import { RuntimeService } from '../../services/runtime.service';
import { SubscriptionService } from '../../services/subscription.service';
import { FormSubmitWorkflowService } from '../../services/workflow-generation/public-api';
import { WorkflowUtilsService } from '../../services/workflow-utils.service';
import { getWorkflowRuntimeElement } from '../../utils/runtime';
import { getElementEntities } from '../elements';

import { RuntimeFacade } from './runtime.facade';
import { WorkflowEffectsService } from './workflow-effects.service';
import { createElementWorkflow, generateDeleteRowWorkflow } from './workflow.actions';

@Injectable()
export class WorkflowEffects {
  private readonly actions$ = inject(Actions);
  private readonly iss = inject(IntelliSenseService);
  private readonly jobsFacade = inject(JobsFacade);
  private readonly pageContextFacade = inject(PageContextFacade);
  private readonly runtimeFacade = inject(RuntimeFacade);
  private readonly runtimeService = inject(RuntimeService);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store<any>);
  private readonly subscriptionService = inject(SubscriptionService);
  private readonly urlParametersFacade = inject(UrlParametersFacade);
  private readonly workflowEffectsService = inject(WorkflowEffectsService);
  private readonly workflowGenerationUtilsService = inject(FormSubmitWorkflowService);
  private readonly workflowLogger = inject(WorkflowLoggerService);
  private readonly workflowsApiService = inject(WorkflowsApiService);
  private readonly workflowStepDebugger = inject(WorkflowStepDebugger);
  private readonly workflowUtilsService = inject(WorkflowUtilsService);

  storePreExecutionWorkflowSchema$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.applyWorkflow),
      filter(({ workflow }) => !!workflow),
      concatLatestFrom(({ workflow }) => [
        this.store.select(runtimeSelectors.selectSchemaById(workflow.id)),
      ]),
      filter(([, existingSchema]) => {
        if (!existingSchema) {
          return true;
        }
        if (!isWorkflowSchema(existingSchema)) {
          return true;
        }
        if (existingSchema.isComputed) {
          return true;
        }
        return false;
      }),
      map(([{ workflow, inputSchema }, existingSchema]) => {
        const outputSchema = this.iss.calculateSchemaForWorkflow(workflow, inputSchema).schema;
        return { workflow, existingSchema, outputSchema };
      }),
      filter(({ existingSchema, outputSchema }) => {
        if (!existingSchema || !isWorkflowSchema(existingSchema)) {
          return true;
        }
        return !isDeepEqual(outputSchema, existingSchema);
      }),
      map(({ workflow, outputSchema }) => {
        const isPaginated = this.runtimeService.isPaginated(workflow.id);
        return runtimeActions.storeRuntimeElementSchema({
          element: getWorkflowRuntimeElement(workflow.id),
          schema: outputSchema,
          isPaginated: isPaginated,
        });
      }),
    ),
  );

  workflowEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.runtimeEvent),
      filter(({ event }) => {
        return event.connection.target.layerType === 'Workflow';
      }),
      mergeMap(({ event }) => {
        return of(event).pipe(
          // Fetch page context for the namespace
          concatMap(() => {
            return this.pageContextFacade.getPageContextForPage(event.ns).pipe(first());
          }),
          mergeMap((pageContext) => {
            const workflow = this.runtimeFacade.getWorkflowById(event.connection.target.id);
            if (shouldWorkflowSubscribeToChanges(workflow)) {
              return this.subscriptionService.socketConfig$.pipe(
                first(),
                map((socketConfig) => {
                  return { event, pageContext, socketConfig, workflow };
                }),
                catchError((err) => {
                  this.workflowLogger.log(
                    'Unable to create subscription listening on workflow data changes',
                    {
                      level: 'error',
                      extra: {
                        err,
                      },
                      workflowId: event.connection.target.id,
                    },
                  );
                  return of({ event, pageContext, socketConfig: undefined, workflow });
                }),
              );
            }
            return of({ event, pageContext, socketConfig: undefined, workflow });
          }),
          mergeMap(({ event, pageContext, socketConfig, workflow }) => {
            const workflowExecutionId = `${workflow?.id}_${crypto.randomUUID()}`;
            if (workflow?.isDraft) {
              // Workflow is a draft (not saved on a server yet) - complete workflow without executing it
              return [
                runtimeActions.connectionStepComplete({ event, workflow, workflowExecutionId }),
              ];
            } else if (workflow && !workflow.isDraft) {
              let input = this.runtimeService.getDataForElement(
                event.connection.source,
                event.connection.eventType,
              );
              const wfTarget = this.workflowEffectsService.getWorkflowTargetElement(
                workflow,
                event.connection,
              );

              // Input data and its schema we need to pass to the first workflow node
              const firstNode = findFirstNodeInWorkflow(workflow);
              const inputsData = this.workflowEffectsService.getDataForWorkflowInputs(workflow);

              const inputSchema = this.workflowEffectsService.getWorkflowInput1Schema(
                workflow,
                event,
                inputsData,
                pageContext,
              );

              // Cleanup duplicated data in MAIN_INPUT_NAME to make pagination work for workflows with inputs / search and filters
              // Pagination is reset when inputs change. Using any filter then trying to navigate to next page was resulting in
              // pagination reset due to MAIN_INPUT_NAME change. This check ensures that no unnecessary input1 is sent.
              // Related task: https://blaze-tech.atlassian.net/browse/BL-4677
              if (
                !isEmptyish(input) &&
                isGetNode(firstNode) &&
                workflow.inputs[MAIN_INPUT_NAME] === undefined &&
                Object.keys(inputsData).some((k) => isEqual(inputsData[k], input))
              ) {
                input = {};
              }

              const actions: Action[] = [
                runtimeActions.applyWorkflow({
                  event,
                  input,
                  inputsData,
                  pageContext,
                  inputSchema,
                  socketConfig,
                  target: wfTarget,
                  workflow,
                  workflowExecutionId,
                }),
              ];

              return actions;
            } else {
              throw new Error(RuntimeErrorMessage.InvalidWorkflow);
            }
          }),
          catchError((error: any) => {
            const workflow = this.runtimeFacade.getWorkflowById(event?.connection?.target?.id);
            return this.workflowEffectsService.getRuntimeErrorActions(
              error,
              event,
              workflow?.id,
              'runtimeEvent',
            );
          }),
        );
      }),
      repeat(),
    ),
  );

  /**
   * Step 2: Workflow needs to be applied. Find first workflow node and begin the cycle of applying workflow nodes.
   */
  applyServerSideWorkflow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.applyWorkflow),
      filter(({ workflow }) => !!workflow),
      concatLatestFrom(({ workflow, pageContext }) => {
        return [
          this.store.select(getElementEntities),
          this.store.select(runtimeSelectors.selectQueries),
          this.store.select(runtimeSelectors.selectPendingPaginations),
          this.store.select(runtimeSelectors.selectConnections),
          this.urlParametersFacade.getPageVariables$(pageContext?.pageId),
          this.workflowEffectsService.getGlobalQueryForWorkflow(workflow),
        ];
      }),
      // Ensure that the same workflow executes sequentially (after previous execution of the same workflow completes).
      groupBy(([action]) => action.workflow?.id),
      mergeMap(($group) =>
        $group.pipe(
          // In case a global query applicable to the workflow has been found, return outputs of that global query filters.
          mergeMap(
            ([
              action,
              uiEntities,
              paginations,
              paginationRequests,
              connections,
              varUrl,
              globalQuery,
            ]) => {
              const execInfo = {
                action,
                connections,
                globalQuery,
                globalQueryContext: undefined,
                paginationRequests,
                paginations,
                uiEntities,
                varUrl,
              };
              if (globalQuery?.elementId && globalQuery?.datasetId) {
                return this.store
                  .select(runtimeSelectors.selectOutputUiById(globalQuery.elementId, 'change'))
                  .pipe(
                    first(),
                    map((globalQueryOutputs) => {
                      return { ...execInfo, globalQueryContext: globalQueryOutputs?.data };
                    }),
                  );
              }
              return of(execInfo);
            },
          ),
          concatMap(
            ({
              action,
              connections,
              globalQuery,
              globalQueryContext,
              paginationRequests,
              paginations,
              uiEntities,
              varUrl,
            }) =>
              of(action).pipe(
                map(
                  ({
                    event,
                    workflow,
                    input,
                    target,
                    pageContext,
                    socketConfig,
                    inputSchema,
                    inputsData,
                    workflowExecutionId,
                    debugMode,
                  }) => {
                    this.workflowLogger.log(`Run workflow ${workflow?.id}`, {
                      workflowExecutionId: workflowExecutionId,
                      extra: {
                        workflow,
                        event,
                        input,
                        target,
                        pageContext,
                        globalQueryContext,
                      },
                    });
                    const overrideInput1 =
                      this.workflowEffectsService.canPageContextOverwriteInput1(
                        event.connection,
                        inputsData,
                        pageContext,
                      );
                    const ctx = this.iss.createRuntimeContext({
                      eventContext: event.eventContext,
                      globalQueryContext,
                      inputsData,
                      overrideInput1,
                      pageContext,
                      urlVars: varUrl,
                    });
                    // This should be ?? instead of || in order to not overwrite input1 when input1 is an empty search string (BL-6612)
                    const nodeInput = ctx.input1 ?? input;
                    this.workflowLogger.log(`Context for the workflow:`, {
                      extra: {
                        ctx,
                        nodeInput,
                      },
                      workflowExecutionId: workflowExecutionId,
                    });
                    const workflowNode = getWorkflowNodeById(workflow?.startNodeId, workflow);

                    const nodePayload: ApplyWorkflowNodePayload = {
                      ctx,
                      event,
                      nodeInput,
                      debugMode,
                      schema: inputSchema,

                      target,
                      userInputs: null,
                      workflow,
                      workflowExecutionId,
                      workflowNode,
                    };
                    return {
                      connections,
                      globalQuery,
                      nodePayload,
                      paginationRequests,
                      paginations,
                      socketConfig,
                      uiEntities,
                    };
                  },
                ),
                concatMap(
                  ({
                    connections,
                    globalQuery,
                    nodePayload,
                    paginationRequests,
                    paginations,
                    socketConfig,
                    uiEntities,
                  }) => {
                    const {
                      event,
                      workflow,
                      workflowNode,
                      target,
                      workflowExecutionId,
                      debugMode,
                    } = nodePayload;
                    let { ctx, nodeInput } = nodePayload;
                    const { schema } = nodePayload;
                    if (!workflow) {
                      throw new Error('Unable to execute workflow. No workflow found.');
                    }

                    const blockedInfo = this.workflowEffectsService.getWorkflowBlockedReason({
                      event,
                      workflow,
                      ctx,
                      uiEntities,
                      connections,
                      target,
                      paginationRequests,
                      workflowExecutionId,
                    });

                    // Workflow not blocked - execute
                    if (!blockedInfo) {
                      const originalNodeInput = nodeInput;

                      return combineLatest({
                        promptOutput: this.workflowEffectsService.showNodePromptOrContinue(
                          workflow,
                          workflowNode,
                          ctx,
                          nodeInput,
                          schema,
                          true,
                        ),
                      }).pipe(
                        mergeMap(({ promptOutput }) => {
                          if (this.workflowEffectsService.wasCancelledByDialog(promptOutput)) {
                            return of(
                              this.workflowEffectsService.cancelNextWorkflowNode(
                                nodePayload,
                                originalNodeInput,
                                workflowNode?.id,
                              ),
                            );
                          } else if (
                            this.workflowEffectsService.canWorkflowBeResumed(nodePayload)
                          ) {
                            const userInputs = this.workflowEffectsService.formatUserInput(
                              promptOutput?.dialogOutputData,
                              schema,
                            );
                            return [
                              this.workflowEffectsService.applyNextWorkflowNode({
                                ...nodePayload,
                                userInputs,
                              }),
                            ];
                          }

                          if (promptOutput.nodeInput) {
                            nodeInput = shallowCopy(promptOutput.nodeInput);
                          }

                          // Merge data from the dialog
                          if (!isDeepEqual(nodeInput, originalNodeInput)) {
                            nodeInput = this.workflowEffectsService.mergeDialogDataWithInput(
                              nodeInput,
                              originalNodeInput,
                            );
                          }

                          ctx = this.iss.updateRuntimeContext(ctx, nodeInput);
                          const returnTotalCount = event.connection.paginationRefresh ?? false;
                          const { pagination, query, groups } =
                            this.workflowEffectsService.createPaginationForServer({
                              globalQuery,
                              paginationRequests,
                              paginations,
                              paginationTarget: target,
                              returnTotalCount,
                              uiEntities,
                              workflow,
                            });
                          this.workflowLogger.log(
                            `Execute workflow ${workflow.id} on server with context:`,
                            {
                              workflowExecutionId: workflowExecutionId,
                              extra: {
                                ctx,
                              },
                            },
                          );

                          const context = this.iss.prepareRuntimeContextForServer(ctx, workflow);
                          let serverExecute$ = this.workflowsApiService
                            .postExecute({
                              allowPolling: isPolledWorkflow(workflow),
                              channelId: socketConfig?.channelId,
                              groups,
                              inputs: context,
                              pagination,
                              query,
                              workflowId: workflow.id,
                              debugMode: action.debugMode,
                            })
                            .pipe(
                              map((result) => {
                                if (hasGroupedData(result) && groups?.length) {
                                  result.data = this.workflowEffectsService.getFlatGroupedData(
                                    result.groupedData,
                                  );
                                }
                                return result;
                              }),
                            );
                          // In case we should skip server execution, return fake response so that FE can continue execution
                          // without unnecessary delay. We send request to the server just so it can log that the given workflow
                          // has been executed but we don't wait for the response.
                          if (this.workflowEffectsService.shouldSkipServerExecution(workflow)) {
                            this.workflowLogger.log(
                              `No server nodes found - skipping server execution.`,
                              {
                                workflowExecutionId: workflowExecutionId,
                              },
                            );
                            serverExecute$.subscribe();
                            serverExecute$ = of({
                              nextNodeId: workflow.startNodeId,
                            } as ExecuteWorkflowResponse);
                          }
                          return serverExecute$.pipe(
                            mergeMap(
                              (execResponse: ExecuteWorkflowResponse | ServerErrorResponse) => {
                                // Is error response or no polling necessary
                                if (
                                  isServerErrorResponse(execResponse) ||
                                  this.workflowUtilsService.hasWorkflowExecutionFailed(
                                    execResponse,
                                  ) ||
                                  !execResponse?.jobId
                                ) {
                                  return of(execResponse);
                                }
                                return this.jobsFacade.pollJobStatusUntilDoneOrStopped<ExecuteWorkflowResponse>(
                                  execResponse.jobId,
                                );
                              },
                            ),
                            filter(
                              (execResponse: ExecuteWorkflowResponse | ServerErrorResponse) => {
                                return !!execResponse;
                              },
                            ),
                            concatMap(
                              (execResponse: ExecuteWorkflowResponse | ServerErrorResponse) => {
                                // Handle "true"/terminating workflow execution errors. This SHOULD NOT be called when
                                // workflow fails on the server but expects frontend to pick up and continue execution.
                                // True terminating errors are non-200 server responses. 200 responses with "error"
                                // property (@see isHttpErrorResponse) should continue executing and not be treated
                                // as terminating errors.
                                if (isServerErrorResponse(execResponse)) {
                                  return this.workflowEffectsService.handleServerWorkflowExecutionError(
                                    {
                                      ctx,
                                      error: execResponse,
                                      event,
                                      schema,
                                      target,
                                      workflow,
                                      workflowExecutionId,
                                      workflowNode,
                                    },
                                  );
                                }

                                return this.workflowEffectsService.handleServerExecution({
                                  ctx,
                                  event,
                                  execResponse,
                                  nodeInput,
                                  pagination,
                                  schema,
                                  socketConfig,
                                  target,
                                  workflow,
                                  workflowExecutionId,
                                  workflowNode,
                                });
                              },
                            ),
                            catchError((error: any) => {
                              // If error happens in this scope, handle it here to use the updated context and node input coming from the dialog
                              return this.workflowEffectsService.handleServerWorkflowExecutionError(
                                {
                                  ctx,
                                  error,
                                  event,
                                  schema,
                                  target,
                                  workflow,
                                  workflowExecutionId,
                                  workflowNode,
                                },
                              );
                            }),
                          );
                        }),
                        startWith(
                          runtimeActions.markPendingUiWorkflow({ event, workflowId: workflow.id }),
                        ), // Mark as pending on the execution start
                        catchError((error: any) => {
                          return this.workflowEffectsService.handleServerWorkflowExecutionError({
                            ctx,
                            error,
                            event,
                            schema,
                            target,
                            workflow,
                            workflowExecutionId,
                            workflowNode,
                          });
                        }),
                      );
                    }

                    const { inputSchema } = action;

                    this.workflowLogger.log(
                      `Execution of workflow ${workflow?.id} has been blocked.`,
                      {
                        extra: { event, workflow, ctx, target, schema: inputSchema },
                        workflowExecutionId: workflowExecutionId,
                      },
                    );
                    this.workflowLogger.log(`Reason for blocking ${workflow?.id}: ${blockedInfo}`, {
                      workflowExecutionId: workflowExecutionId,
                      toaster: debugMode,
                    });
                    const actions: Action[] = [];
                    if (target?.id && !this.runtimeService.isElementInitialized(target.id)) {
                      actions.push(runtimeActions.uiWorkflowExecuted({ elementId: target.id }));
                    }

                    const isBlockedWithData =
                      event.eventContext?.isEmptyDataSelection &&
                      this.schemaService.dataMatchesSchema(event.data, inputSchema);
                    const outputData = isBlockedWithData
                      ? Array.isArray(event.data)
                        ? event.data
                        : [event.data]
                      : undefined;

                    actions.push(
                      runtimeActions.connectionStepComplete({
                        event,
                        workflow,
                        phase: isBlockedWithData
                          ? WorkflowPhase.BlockedWithData
                          : WorkflowPhase.Blocked,
                        phaseInfo: blockedInfo,
                        workflowExecutionId,
                        outputData,
                        updateSource: isBlockedWithData ? 'workflow' : undefined,
                      }),
                    );
                    return actions;
                  },
                ),
                catchError((error: any) => {
                  const actions: Action[] = [];
                  if (
                    action.target?.id &&
                    !this.runtimeService.isElementInitialized(action.target.id)
                  ) {
                    actions.push(
                      runtimeActions.uiWorkflowExecuted({ elementId: action.target.id }),
                    );
                  }
                  actions.push(
                    ...this.workflowEffectsService.getRuntimeErrorActions(
                      error,
                      action.event,
                      action.workflow?.id,
                      'applyWorkflow',
                    ),
                  );
                  return actions;
                }),
              ),
          ),
        ),
      ),

      repeat(),
      this.workflowStepDebugger.interceptStep(),
    ),
  );

  /**
   * Resumes worflow execution on the server. Initiated by any of the nodes if resumeToken is found in their
   * ApplyWorkflowNodePayload.
   */
  resumeWorkflowExecutionOnServer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.resumeWorkflowNode),
      mergeMap((nodePayload: ApplyWorkflowNodePayload) => {
        const {
          ctx,
          event,
          isWorkflowCancelled,
          nodeInput,
          prevNodeId,
          resumeToken,
          schema,
          target,
          userInputs,
          workflow,
          workflowExecutionId,
          workflowNode,
        } = nodePayload;

        // Attempt to resume workflow on the server
        if (workflow?.id && resumeToken && !isWorkflowCancelled) {
          if (isNullish(nodeInput)) {
            this.workflowLogger.log(
              `Attempting to resume workflow with nodeInput: ${nodeInput}. Prev node: ${prevNodeId}`,
              {
                workflowExecutionId: workflowExecutionId,
                logToSentry: true,
              },
            );
          }
          const inputs: Record<string, DatasetResponse> = {
            $$input1: {
              data: Array.isArray(nodeInput) ? nodeInput : isNullish(nodeInput) ? [] : [nodeInput], // [undefined] and [null] are not valid inputs. Default to empty list.
              schema: schema ? convertWorkflowSchemaToServerDatasetSchema(schema) : schema,
            },
          };

          const localVariables = this.iss.getLocalVarsForResume(this.runtimeFacade.projectId);
          return this.workflowsApiService
            .postResumeWorkflow(workflow.id, {
              inputs,
              localVariables,
              resumeToken,
              userInputs,
            })
            .pipe(
              mergeMap((execResponse: ExecuteWorkflowResponse | ServerErrorResponse) => {
                // Is error response or no polling necessary
                if (isServerErrorResponse(execResponse) || !execResponse?.jobId) {
                  return of(execResponse);
                }
                return this.jobsFacade.pollJobStatusUntilDoneOrStopped<ExecuteWorkflowResponse>(
                  execResponse.jobId,
                );
              }),
              filter((execResponse: ExecuteWorkflowResponse | ServerErrorResponse) => {
                return !!execResponse;
              }),
              mergeMap((execResponse: ExecuteWorkflowResponse | ServerErrorResponse) => {
                if (isServerErrorResponse(execResponse)) {
                  return this.workflowEffectsService.handleServerWorkflowExecutionError({
                    ctx,
                    error: execResponse,
                    event,
                    schema,
                    target,
                    workflow,
                    workflowExecutionId,
                    workflowNode,
                  });
                }
                return this.workflowEffectsService.handleServerExecution({
                  execResponse,
                  workflow,
                  workflowExecutionId,
                  workflowNode,
                  schema,
                  nodeInput,
                  ctx,
                  pagination: null,
                  event,
                  target,
                });
              }),
              catchError((error: any) => {
                // If error happens in this scope, handle it here to use the updated context and node input coming from the dialog
                return this.workflowEffectsService.handleServerWorkflowExecutionError({
                  ctx,
                  error,
                  event,
                  schema,
                  target,
                  workflow,
                  workflowExecutionId,
                  workflowNode,
                });
              }),
            );
        }
        // Failed to resume workflow due to missing information. Try locally.
        console.warn(
          `Unable to resume workflow on the server side. Attempting to complete it locally.`,
        );
        const nextNodePayload: ApplyWorkflowNodePayload = {
          ...nodePayload,
          resumeToken: undefined,
        };
        return [runtimeActions.applyWorkflowNode(nextNodePayload)];
      }),
    ),
  );

  generateDeleteRowWorkflow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(generateDeleteRowWorkflow),
      withLatestFrom(this.store.select(getElementEntities)),
      filter(([{ actionPath, elementId }, elements]) => {
        const element = elements[elementId] as CanvasElement;
        const currentWorkflowId =
          hasProps(element) && (getValueByPath(element.props, `${actionPath}.targetId`) as string);
        return !currentWorkflowId;
      }),
      map(([{ elementId }, elements]) => {
        const element = elements[elementId] as CanvasElement;
        const dataSource = hasDataProps(element)
          ? getActiveBinding(element.props.data, 'value')?.dataSource
          : undefined;
        if (!dataSource) {
          throw new Error('Could not create node: data source not configured');
        }

        const { actionNode: node } = this.workflowGenerationUtilsService.getActionNode(
          'delete',
          dataSource,
        );
        if (!node) {
          throw new Error('Could not create node');
        }

        if (isElementType(element, 'calendar') && isDeleteNode(node)) {
          node.confirmationMessage = 'Do you want to delete the selected appointment?';
        }

        return createElementWorkflow({
          elementId,
          inputSchemas: [],
          nodes: [node],
          workflowIdPath: 'content.itemDelete.targetId',
          ns: getNamespaceId(elementId),
        });
      }),
    ),
  );
}
